import * as React from 'react'
import { TextInput } from '@/react/components'
import { css } from '@emotion/react'
import { getTranslatedString } from '@/utils'

import { getStartingValue, NoteElement } from './index'
import { IFieldData, IFormMemory } from '@/react/types/mobileForms'

interface ITextFieldProps {
  fieldData: IFieldData
  formMemory: IFormMemory
  possibleGroupData: unknown
  onChange: (value: unknown) => void
}

export const TextField = ({
  fieldData,
  formMemory,
  possibleGroupData,
  onChange,
}: ITextFieldProps) => {
  const [error, setError] = React.useState(false)
  const [currentOption, setCurrentOption] = React.useState(
    fieldData.nested_option,
  )
  const prevFieldIdRef = React.useRef<string | number>(fieldData.id)

  function callback(value: unknown) {
    onChange({ value, possibleGroupData, id: fieldData.id })
    setError(!value && fieldData.required)
  }

  const baseStyle = getBaseStyle()

  const startingText = getStartingValue(
    possibleGroupData,
    formMemory,
    fieldData,
    'text',
  ) as string

  React.useEffect(() => {
    if (fieldData.required && !startingText) {
      setError(true)
    }
  }, [])

  const clearFieldValues = React.useCallback(
    (fieldId: string | number) => {
      const field = formMemory[fieldId as keyof typeof formMemory] as
        | IFormMemory
        | undefined
      if (!field) return

      if (possibleGroupData) {
        const groupField = field[possibleGroupData as keyof typeof field] as
          | IFormMemory
          | undefined
        if (groupField?.localAnswer) {
          groupField.localAnswer.text = ''
          groupField.localAnswer.value = ''
        }
      } else if (field.localAnswer) {
        field.localAnswer.text = ''
        field.localAnswer.value = ''
      }
    },
    [formMemory, possibleGroupData],
  )

  // Track option changes
  React.useEffect(() => {
    if (currentOption !== fieldData.nested_option) {
      setCurrentOption(fieldData.nested_option)

      // Only clear if we have a nested option
      if (fieldData.nested_option) {
        clearFieldValues(fieldData.id)

        const parentField = Object.values(formMemory).find(
          (field: { answer_options?: Array<{ id: string | number }> }) =>
            field.answer_options?.some(
              (option: { id: string | number }) =>
                option.id === fieldData.nested_option,
            ),
        ) as IFormMemory | undefined

        if (parentField?.localAnswer) {
          parentField.localAnswer.text = ''
          parentField.localAnswer.value = ''
          parentField.localAnswer.chosen_answer = ''
        }

        if (fieldData.answer_options?.length > 0) {
          fieldData.answer_options.forEach((option) => {
            option.nested_fields?.forEach((nestedField) => {
              clearFieldValues(nestedField.id)
            })
          })
        }

        onChange({ value: '', possibleGroupData, id: fieldData.id })
      }
    }
  }, [fieldData.nested_option, clearFieldValues])

  // Clear values when fieldId changes
  React.useEffect(() => {
    if (prevFieldIdRef.current !== fieldData.id) {
      // Only clear the previous field's values
      clearFieldValues(prevFieldIdRef.current)
      prevFieldIdRef.current = fieldData.id
    }
  }, [fieldData.id, clearFieldValues])

  return (
    <section id={String(fieldData.id)} css={baseStyle}>
      <div className="label-holder">
        <label css={css({ color: 'inherit' })}>
          {getTranslatedString(fieldData.name)}
          {fieldData.required ? ' *' : null}
        </label>
        {fieldData.note ? <NoteElement noteText={fieldData.note} /> : null}
      </div>
      <TextInput
        defaultValue={startingText}
        valueCallback={callback}
        width={'400px'}
        waitTime={0}
        textarea
        error={error}
        fieldId={String(fieldData.id)}
        textLimit={Infinity}
      />
    </section>
  )
}

function getBaseStyle() {
  return css({
    marginBottom: 15,
    marginTop: 10,
  })
}
